import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'

import type { WebhookAuthKeys } from '@mangovoice/types'

import type { ApiError } from '../api/types'
import type { WebhooksApi } from '../api/webhooksApi'

export const authKeysQueryKey = (pbxId: number) => ['auth-keys', pbxId] as const

export const useGetAuthKeys = ({
  api,
  pbxId,
}: {
  api: WebhooksApi
  pbxId: number
}): UseQueryResult<WebhookAuthKeys | null, ApiError> => {
  return useQuery<WebhookAuthKeys | null, ApiError>({
    queryKey: authKeysQueryKey(pbxId),
    queryFn: async () => {
      const response = await api.getAuthKeys(pbxId)
      if (response.status === 404) return null
      if (!response.ok) return Promise.reject(response.data as ApiError)
      return response.data ?? null
    },
    enabled: pbxId > 0,
    retry: false,
  })
}

const useWriteAuthKeys = (
  apiMethod: (pbxId: number) => ReturnType<WebhooksApi['generateAuthKeys']>,
  pbxId: number,
): UseMutationResult<WebhookAuthKeys, ApiError, void> => {
  const queryClient = useQueryClient()
  return useMutation<WebhookAuthKeys, ApiError, void>({
    mutationFn: async () => {
      const response = await apiMethod(pbxId)
      if (!response.ok || !response.data)
        return Promise.reject(response.data as ApiError)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(authKeysQueryKey(pbxId), data)
    },
  })
}

export const useGenerateAuthKeys = ({
  api,
  pbxId,
}: {
  api: WebhooksApi
  pbxId: number
}): UseMutationResult<WebhookAuthKeys, ApiError, void> =>
  useWriteAuthKeys((id) => api.generateAuthKeys(id), pbxId)

export const useRegenerateAuthKeys = ({
  api,
  pbxId,
}: {
  api: WebhooksApi
  pbxId: number
}): UseMutationResult<WebhookAuthKeys, ApiError, void> =>
  useWriteAuthKeys((id) => api.regenerateAuthKeys(id), pbxId)

export const useDeleteAuthKeys = ({
  api,
  pbxId,
}: {
  api: WebhooksApi
  pbxId: number
}): UseMutationResult<void, ApiError, void> => {
  const queryClient = useQueryClient()
  return useMutation<void, ApiError, void>({
    mutationFn: async () => {
      const response = await api.deleteAuthKeys(pbxId)
      if (!response.ok) return Promise.reject(response.data as ApiError)
    },
    onSuccess: () => {
      queryClient.setQueryData(authKeysQueryKey(pbxId), null)
    },
  })
}
